import { FieldWrapper } from '@/src/modules/smarty/StyledComponents'
import { SelectInput } from '@/src/common/components/elements/Forms/SelectWithLabel'
import { useState } from 'react'
import Button from '@/src/common/components/marketing/buttons/Button'
import { useBasicGiftCardContext } from '@/src/common/contexts/BasicGiftCardContext'
import { styled, theme } from '@/src/stitches.config'
import Image from 'next/image'
import { assembleCategoriesAndFilterDesigns } from '@/src/common/utilities/assembleCategoriesAndFilterDesigns'
import { TGreetingCardDesign } from '@/src/modules/build-a-card/types'
import { ClipLoader } from 'react-spinners'
import { LoadingOverlay } from '@/src/modules/checkout/forms/StyledComponents'
import StyledErrorMessage from '@/src/common/components/elements/Alerts/StyledErrorMessage'
import { HeaderParagraphText } from '@/src/modules/build-a-card/StyledComponents'

const DesignsWrapper = styled('div', {
    display: 'flex',
    flexWrap: 'wrap',
    minHeight: 200,
    maxHeight: 730,
    my: 30,
    gap: '2%',
    overflowY: 'auto',
})

const Design = styled('div', {
    width: '48%',
    aspectRatio: '3/4',
    border: '3px solid $mediumGray',
    br: 5,
    mb: 10,
    overflow: 'hidden',
    cursor: 'pointer',
    '@sm': {
        width: '30%',
    },
    '&:hover, &:focus, .active-image': {
        background: `linear-gradient(white, white) padding-box, linear-gradient(83.53deg, ${theme.colors.primary} 18.47%, ${theme.colors.secondary} 78%) border-box`,
        border: '3px solid transparent',
    },
    '& img': {
        display: 'block',
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
})

const GreetingCardDesignOptions = () => {
    const {
        setCurrentStep,
        greetingCardUuid,
        setGreetingCardUuid,
        setGreetingCardDesignName,
        greetingCards,
    } = useBasicGiftCardContext()
    const [selectedCategory, setSelectedCategory] = useState<string>('empty')
    const [errorText, setErrorText] = useState<string>('')
    const [isLoading, setIsLoading] = useState<boolean>(false)

    // get the greeting card designs and categories
    const { filteredDesigns, categories } = assembleCategoriesAndFilterDesigns({
        items: greetingCards,
        selectedCategory: selectedCategory,
    })

    function validateAndSetStep() {
        if (greetingCardUuid) {
            setCurrentStep(4)
        }
        setErrorText('Please select a greeting card design')
        setIsLoading(false)
    }

    return (
        <>
            <HeaderParagraphText>
                Select a design for the cover of your greeting card:
            </HeaderParagraphText>
            <FieldWrapper style={{ width: 375, maxWidth: '100%' }}>
                <SelectInput
                    label={'Categories'}
                    className={'categories'}
                    value={selectedCategory}
                    options={categories}
                    onValueChange={(value: string) => {
                        setSelectedCategory(value)
                    }}
                    width={360}
                />
            </FieldWrapper>
            <DesignsWrapper id={'designs-wrapper'}>
                {!filteredDesigns && (
                    <LoadingOverlay>
                        <ClipLoader color={'black'} loading={true} size={30} />
                    </LoadingOverlay>
                )}{' '}
                {filteredDesigns.map((design: TGreetingCardDesign) => {
                    return (
                        <Design
                            key={`${design.slug}`}
                            onClick={() => {
                                setErrorText('')
                                setGreetingCardUuid(design.uuid)
                                setGreetingCardDesignName(design.designName)
                            }}
                            className={
                                greetingCardUuid === design.uuid
                                    ? 'active-button-gradient'
                                    : ''
                            }
                            style={{ borderWidth: 3 }}
                        >
                            <Image
                                alt={`Greeting Card - ${design.slug}`}
                                src={design?.greeting_card_preview_url ?? ''}
                                width={256}
                                height={331}
                            />
                        </Design>
                    )
                })}
            </DesignsWrapper>
            {errorText && (
                <StyledErrorMessage
                    errorText={errorText}
                    style={{ marginBottom: 20 }}
                />
            )}
            <Button
                label={'Continue'}
                textTransform={'uppercase'}
                onClick={() => {
                    setIsLoading(true)
                    validateAndSetStep()
                }}
                width={'standard'}
                showArrow={true}
                isLoading={isLoading}
                disabled={isLoading}
            />
        </>
    )
}

export default GreetingCardDesignOptions
